/**
 * Toast 全局状态（zustand）。
 *
 * 用法：
 *   import { toast } from "@/lib/toast-store";
 *   toast.success("操作成功");
 *   toast.error("失败了");
 */
"use client";

import { create } from "zustand";

export type ToastVariant = "success" | "error" | "info" | "warning";

export type ToastItem = {
  id: string;
  variant: ToastVariant;
  message: string;
  duration: number;
};

type State = {
  toasts: ToastItem[];
  push: (t: Omit<ToastItem, "id">) => string;
  remove: (id: string) => void;
};

let _idSeed = 0;
function genId() {
  _idSeed += 1;
  return `t_${Date.now().toString(36)}_${_idSeed}`;
}

export const useToastStore = create<State>((set) => ({
  toasts: [],
  push: (t) => {
    const id = genId();
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
    return id;
  },
  remove: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

function show(
  variant: ToastVariant,
  message: string,
  duration = 2500,
): string {
  const id = useToastStore.getState().push({ variant, message, duration });
  if (duration > 0) {
    setTimeout(() => {
      useToastStore.getState().remove(id);
    }, duration);
  }
  return id;
}

export const toast = {
  success: (msg: string, duration?: number) => show("success", msg, duration),
  error: (msg: string, duration?: number) => show("error", msg, duration),
  info: (msg: string, duration?: number) => show("info", msg, duration),
  warning: (msg: string, duration?: number) => show("warning", msg, duration),
  dismiss: (id: string) => useToastStore.getState().remove(id),
  clear: () => useToastStore.setState({ toasts: [] }),
};
